import React, {useContext, useEffect, useRef} from 'react';
import {getConnectedEdges, Handle, NodeToolbar, Position, useReactFlow} from 'reactflow';
import {AccountContext} from '@/context/AccountProvider';
import {NodeContext} from '@/context/NodeProvider';
import {t} from "i18next";
import {Link} from "react-router-dom";
import {deleteAccount} from "@/service/accountApi";
import {toast} from "react-toastify";
import Cookies from "universal-cookie";
import {isTreeAdmin} from "@/utils/permissions";

const MemberNode = ({data, isConnectable, id}) => {
	const cookies = new Cookies();
	const instance = useReactFlow();

	const handleASourceRef = useRef(null);
	const handleATargetRef = useRef(null);
	const handleBSourceRef = useRef(null);
	const handleBTargetRef = useRef(null);
	const handleCSourceRef = useRef(null);
	const handleCTargetRef = useRef(null);
	const handleDSourceRef = useRef(null);
	const handleDTargetRef = useRef(null);

	const {account} = useContext(AccountContext);
	const {
		selectedNode,
		relativeNode,
		treeRole,
		setSelectedNodeForRoleManagement,
		setShowRoleManagementModal
	} = useContext(NodeContext);

	function formatDate(dateString) {
		// Parse the ISO date string into a Date object
		const date = new Date(dateString);

		// Get the day of the month
		const day = date.getUTCDate();

		// Add the appropriate ordinal suffix to the day
		const dayWithSuffix = day + (day % 10 === 1 && day !== 11 ? 'st' :
			day % 10 === 2 && day !== 12 ? 'nd' :
				day % 10 === 3 && day !== 13 ? 'rd' : 'th');

		// Get the month as a short string
		const month = date.toLocaleString('en-US', {month: 'short'});

		// Get the year
		const year = date.getUTCFullYear();

		// Format the final output
		return `${dayWithSuffix} ${month}, ${year}`;
	}

	useEffect(() => {
		const observers = [];

		const targetToSourceMapping = {
			"a-target": handleASourceRef,
			"b-target": handleBSourceRef,
			"c-target": handleCSourceRef,
			"d-target": handleDSourceRef,
		};

		// Function to observe a single handle
		const observeHandle = (handleRef, handleId) => {
			if (handleRef.current) {
				const observer = new MutationObserver((mutations) => {
					mutations.forEach((mutation) => {
						if (mutation.attributeName === "class") {
							// Fire your function here
							// console.log(`Class list changed for ${handleId}:`, handleRef.current.className);
							// I want to pass the handleRef or the source handle and target handle both to the function
							if (targetToSourceMapping[handleId] && targetToSourceMapping[handleId].current) {
								onHandleClassChange(targetToSourceMapping[handleId], handleRef.current.className);
							}
						}
					});
				});

				observer.observe(handleRef.current, {attributes: true, attributeFilter: ["class"]});
				observers.push(observer);
			}
		};

		// List of refs and their IDs
		const handleRefs = [
			{ref: handleASourceRef, id: "a-source"},
			{ref: handleATargetRef, id: "a-target"},
			{ref: handleBSourceRef, id: "b-source"},
			{ref: handleBTargetRef, id: "b-target"},
			{ref: handleCSourceRef, id: "c-source"},
			{ref: handleCTargetRef, id: "c-target"},
			{ref: handleDSourceRef, id: "d-source"},
			{ref: handleDTargetRef, id: "d-target"},
		];

		// Observe each handle
		handleRefs.forEach(({ref, id}) => observeHandle(ref, id));

		return () => {
			// Disconnect all observers on cleanup
			observers.forEach((observer) => observer.disconnect());
		};
	}, []);

	const onHandleClassChange = (sourceHandleRef, newClass) => {
		if (sourceHandleRef.current) {
			if (newClass.includes("react-flow__handle-connecting") || newClass.includes("react-flow__handle-valid")) {
				sourceHandleRef.current.style.background = '#FF6060';
			} else {
				sourceHandleRef.current.style.background = '#90EE90';
			}
		}
	}

	const handleDeleteNode = async () => {
		// Delete from database
		const deleteNodeResponse = await deleteAccount(data.accountId, id);

		if (deleteNodeResponse.success) {
			// Delete the node from react-flow
			instance.deleteElements({nodes: [{id: id}]});

			// Delete the relations connected to this node from react-flow
			// Get all the edges connected to this node
			// There is no getElements method in react-flow

			instance.deleteElements({edges: getConnectedEdges([data], instance.getEdges())});

			toast.dismiss();
			toast.success(deleteNodeResponse.message, {
				autoClose: 3000,
			});
		}
	}

	return (
		<div
			className={`
                min-h-[10vh] max-h-[10vh] h-[10vh]
                group rounded-full shadow-lg 
                p-[1vh] pr-[2vh] gap-[1.5vh]
                flex flex-row items-center justify-between
                ${selectedNode?._id === id ? "border-4 border-[#FFB485]" : "border-4 border-cyan-100"}
                transition-all duration-100 ease-in-out
            `}
			style={{backgroundColor: data.gender === "Male" ? "#0075EA" : "#A020F0"}}
		>
			{
				account.nodeId === id &&
				<img src='/assets/sideArrow.png'
				     alt="member image"
				     className="absolute left-4 rotate-90 overflow h-5 w-5 -top-8"
				/>
			}

			{
				relativeNode.includes(id) &&
				<img src='/assets/down-arrow.png'
				     alt="member image"
				     className="absolute right-2 animate-bounce overflow h-10 w-10 -top-20"
				/>
			}

			<div
				className={`z-10 h-full aspect-square overflow-hidden 
                    flex items-center justify-center rounded-full 
                    border-2 border-white ${data.imageUrlBg}`}
			>
				{
					data.imageUrl.length <= 2
						? (
							<p className={"text-white font-custom-mono-bold text-[2.25vh] leading-tight"}>
								{data.imageUrl.toUpperCase()}
							</p>
						)
						: (
							<img
								src={!(data.imageUrl === '') ? data.imageUrl : 'assets/default-pfp.png'}
								referrerPolicy={"no-referrer"}
								alt="Member Profile Picture"
							/>
						)
				}
			</div>

			<div className="h-full flex flex-col justify-center items-start gap-[0.25vh]">
				{/* First Name | Middle Name */}
				<p className="text-[2.25vh] font-custom-semi-bold text-white leading-tight">
					{data.firstName + ' ' + data.middleName}
				</p>

				{/* First Name | Middle Name - Devanagari */}
				<p className="text-[2.25vh] font-custom-semi-bold text-white leading-tight">
					{data.firstNameDevanagariScript + ' ' + data.middleNameDevanagariScript}
				</p>

				{/* D.O.B. (Optional) */}
				{/*<p className="text-[1.5vh] font-custom-medium text-white">*/}
				{/*    {t("SCREEN.MEMBER_NODE.DOB_PREFIX")}&nbsp;*/}
				{/*    <span>{formatDate(data.dateOfBirth)}</span>*/}
				{/*</p>*/}
			</div>

			<div className="h-full flex items-center justify-center gap-[0.2vh]">
				{
					data.accountId === account.familyTreeAdminId &&
					<img
						className={"w-[4vh] aspect-auto"}
						src={'assets/primary-admin-icon.svg'}
						referrerPolicy={"no-referrer"}
						alt="Admin"
					/>
				}

				{/*{*/}
				{/*    data.generation && (*/}
				{/*        <div*/}
				{/*            className={`z-10 h-full aspect-square overflow-hidden */}
				{/*                flex items-center justify-center rounded-full*/}
				{/*                border-2 border-white bg-[#292D32]`}*/}
				{/*        >*/}
				{/*            <p className={"text-white font-custom-mono-bold text-[2.25vh] leading-tight"}>*/}
				{/*                {data.generation}*/}
				{/*            </p>*/}
				{/*        </div>*/}
				{/*    )*/}
				{/*}*/}

				{
					data.deceased &&
					<img
						className={"w-[4vh] aspect-auto"}
						src={'assets/deceased-icon-filled-3.png'}
						referrerPolicy={"no-referrer"}
						alt="Deceased"
					/>
				}
			</div>

			<NodeToolbar
				position={Position.Bottom}
				className="rounded-lg flex flex-row items-center justify-between"
			>
				<div className="flex content-center">
					{/* Profile Button */}
					<Link
						className="flex flex-row items-center justify-between
                        mr-[0.5vw] px-[0.75vw] py-[0.75vh]
                        bg-green-400 rounded-lg
                        hover:bg-green-600
                        focus:ring-4 focus:outline-none focus:ring-blue-300
                        transition-all duration-200 ease-in-out"
						to={data.accountId === account._id ? "/personal-profile" : `/public-profile/${data.accountId}`}
						onClick={(e) => {
							// console.log("(MemberNode.jsx) lastVisitedNodeId: ", id);
							cookies.set("lastVisitedNodeId", id, {path: "/"});
						}}
					>
						<img
							src="/assets/profile-button-icon-filled-dark.png"
							alt="Edit"
							className="w-[1vw] h-[1vw]"
						/>
						<p className={"ml-1 text-[1vw] font-custom-medium"}>
							{t("SCREEN.MEMBER_NODE.PROFILE")}
						</p>
					</Link>

					{/* Delete Node Button */}
					{account.nodeId !== id && isTreeAdmin(account, treeRole) &&
						<button
							onClick={handleDeleteNode}
							className="flex flex-row items-center justify-between
                                ml-[0.5vw] px-[0.75vw] py-[0.75vh] focus:outline-none
                                bg-red-500 rounded-lg border border-gray-200
                                hover:bg-red-700 hover:text-blue-700
                                focus:z-10 focus:ring-4 focus:ring-gray-100
                                transition-all duration-200 ease-in-out"
						>
							<img
								src="/assets/delete-icon-filled-light.png"
								alt="Delete"
								className="w-[1vw] h-[1vw]"
							/>
							<p className={"ml-1 text-[1vw] font-custom-medium text-white"}>
								{t("SCREEN.MEMBER_NODE.DELETE")}
							</p>
						</button>
					}

					{/* Manage Role Button — visible to SUPER_ADMIN, ADMIN, or PLATFORM SUPER-USER */}
					{account.nodeId !== id && (isTreeAdmin(account, treeRole) || account.primaryRole === "SUPER-USER") && (
						<button
							onClick={() => {
								setSelectedNodeForRoleManagement({
									nodeId: id,
									accountId: data.accountId,
									firstName: data.firstName,
									middleName: data.middleName,
									imageUrl: data.imageUrl,
									imageUrlBg: data.imageUrlBg,
								});
								setShowRoleManagementModal(true);
							}}
							className={`flex flex-row items-center justify-between
								ml-[0.5vw] px-[0.75vw] py-[0.75vh] focus:outline-none
								bg-amber-500 rounded-lg border border-gray-200
								hover:bg-amber-600
								focus:z-10 focus:ring-4 focus:ring-gray-100
								transition-all duration-200 ease-in-out`}
						>
							<img
								src="/assets/admin-icon-2.png"
								alt="Manage Role"
								className="w-[1vw] h-[1vw]"
							/>
							<p className="ml-1 text-[1vw] font-custom-medium text-white">
								{t("SCREEN.MEMBER_NODE.MANAGE_ROLE")}
							</p>
						</button>
					)}
				</div>
			</NodeToolbar>

			<Handle
				ref={handleATargetRef}
				type="target"
				style={{background: '#90EE90', width: 10, height: 10}}
				id="a"
				position={Position.Top}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleASourceRef}
				type="source"
				style={{background: '#90EE90', width: 10, height: 10}}
				id="a"
				position={Position.Top}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleBTargetRef}
				type="target"
				position={Position.Left}
				id="b"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleBSourceRef}
				type="source"
				position={Position.Left}
				id="b"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleCTargetRef}
				type="target"
				position={Position.Right}
				id="c"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleCSourceRef}
				type="source"
				position={Position.Right}
				id="c"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleDTargetRef}
				type="target"
				position={Position.Bottom}
				id="d"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
			<Handle
				ref={handleDSourceRef}
				type="source"
				position={Position.Bottom}
				id="d"
				style={{background: '#90EE90', width: 10, height: 10}}
				isConnectable={isConnectable}
			/>
		</div>
	);
};
export default MemberNode;
